import { ESOCIAL_VALIDATION_STATUS } from '../esocial';
import { isNumeric, parseNumeric } from '../helpers/number';
import { DjangoTimeStamped, TimeStamped } from './../django';
import { DjangoEmployer, Employer, parseEmployer } from './../employer';
import { applyTimezone, parseDate } from './../helpers/date';
import { DjangoUser, User, parseUser } from './../user';
import { CompiledBacen, DjangoCompiledBacen } from './bacen/interfaces';
import { parseCompiledBacen, parseDjangoCompiledBacen } from './bacen/parsers';
import {
  Concentre,
  DetailedConcentre,
  DjangoConcentre,
  DjangoDetailedConcentre,
} from './concentre/interfaces';
import {
  parseConcentre,
  parseDetailedConcentre,
  parseDjangoConcentre,
  parseDjangoDetailedConcentre,
} from './concentre/parsers';

export function parseEmployee(data?: string | Employee | DjangoEmployee) {
  if (data) {
    return new Employee(data);
  } else {
    return undefined;
  }
}

export class Employee implements TimeStamped {
  id: string;
  isActive?: boolean;
  financialConsultantApprovalEnabled?: boolean;
  loanInstallmentsTotal?: number;
  paycheckAdvancesTotal?: number;
  netEarnings?: number;
  score?: number;
  cluster?: string;
  currency?: string;
  automaticCreditLimit?: number;
  manualCreditLimit?: number;
  creditLimit?: number;
  creditBalance?: number;
  debtBalance?: number;
  paycheckAdvanceBalance?: number;
  salary?: number;
  role?: string;
  identificationNumber?: string;
  hiringAt: Date;
  layOffAt?: Date;
  payrollDeductions?: number;
  user?: User;
  employer?: Employer;
  esocialValidationStatus?: ESOCIAL_VALIDATION_STATUS;
  contractId?: string;
  creditUpdatedAt?: Date;
  esocialId?: string;
  salaryNotFound?: boolean;
  maximumInstallmentAmount?: number;
  currentMaximumInstallmentAmount?: number;
  simulationAvailableAt?: Date;
  paycheckAdvanceAvailableAt?: Date;
  status?: string;
  detailedConcentre?: DetailedConcentre;
  concentre?: Concentre;
  corporateEmail?: string;
  compiledBacen?: CompiledBacen;
  bacen?: any;
  maximumPaycheckAdvanceAmount?: number;
  minimumPaycheckAdvanceAmount?: number;
  freeRequestLimitPaycheckAdvance?: number;
  totalLoanDebtBalance?: number;
  totalPaycheckAdvanceDebtBalance?: number;
  hasChargingFileDeductions?: boolean;
  canChangeChargingFile?: boolean;
  hasProposalInProgress?: boolean;
  department?: string;
  refiCreditBalance?: number;
  // DJANGO TIMESTAMPED
  created: Date;
  modified: Date;

  constructor(data: string | Employee | DjangoEmployee = {}) {
    if (typeof data === 'string') {
      bindConstructorDataToInstance(this, { id: data });
    } else {
      bindConstructorDataToInstance(this, data);
    }
  }
}

//////////////////////
/// DJANGO SUPPORT ///
//////////////////////
export interface DjangoEmployee extends DjangoTimeStamped {
  id?: string;
  is_active?: boolean;
  financial_consultant_approval_enabled?: boolean;
  loan_installments_total?: number;
  paycheck_advances_total?: number;
  net_earnings?: number;
  score?: number;
  cluster?: string;
  currency?: string;
  automatic_credit_limit?: number;
  manual_credit_limit?: number;
  credit_limit?: number;
  credit_balance?: string;
  debt_balance?: number;
  paycheck_advance_balance?: string;
  salary?: string;
  role?: string;
  detailed_concentre?: DjangoDetailedConcentre;
  concentre?: DjangoConcentre;
  identification_number?: string;
  hiring_at?: string;
  lay_off_at?: string;
  payroll_deductions?: number;
  user?: string | DjangoUser;
  employer?: DjangoEmployer | string;
  esocial_validation_status?: ESOCIAL_VALIDATION_STATUS;
  contract_id?: string;
  credit_updated_at?: string;
  esocial_id?: string;
  salary_not_found?: boolean;
  maximum_installment_amount?: number;
  current_maximum_installment_amount?: number;
  simulation_available_at?: string;
  paycheck_advance_available_at?: string;
  status?: string;
  corporate_email?: string;
  compiled_bacen?: DjangoCompiledBacen;
  bacen?: any;
  maximum_paycheck_advance_amount?: number;
  minimum_paycheck_advance_amount?: number;
  free_request_limit_paycheck_advance?: number;
  total_loan_debt_balance?: number;
  total_paycheck_advance_debt_balance?: number;
  has_charging_file_deductions?: boolean;
  can_change_charging_file?: boolean;
  has_proposal_in_progress?: boolean;
  department?: string;
  refi_credit_balance?: number;
  // DJANGO TIMESTAMPED
  created?: string;
  modified?: string;
}

///////////////
/// PRIVATE ///
///////////////
function bindConstructorDataToInstance(instance: Employee, data: Employee | DjangoEmployee) {
  const jsData = bindDjangoPropsToJs(data);
  instance.id = jsData.id;
  instance.isActive = jsData.isActive;
  instance.financialConsultantApprovalEnabled = jsData.financialConsultantApprovalEnabled;
  instance.netEarnings = jsData.netEarnings;
  instance.loanInstallmentsTotal = jsData.loanInstallmentsTotal;
  instance.paycheckAdvancesTotal = jsData.paycheckAdvancesTotal;
  instance.currency = jsData.currency;
  instance.score = jsData.score;
  instance.cluster = jsData.cluster;
  instance.detailedConcentre = jsData.detailedConcentre;
  instance.concentre = jsData.concentre;
  instance.compiledBacen = jsData.compiledBacen;
  instance.bacen = jsData.bacen;
  instance.automaticCreditLimit = jsData.automaticCreditLimit;
  instance.manualCreditLimit = parseNumeric(jsData.manualCreditLimit);
  instance.creditLimit = jsData.creditLimit;
  instance.creditBalance = parseNumeric(jsData.creditBalance);
  instance.debtBalance = jsData.debtBalance;
  instance.paycheckAdvanceBalance = parseNumeric(jsData.paycheckAdvanceBalance);
  instance.salary = parseNumeric(jsData.salary);
  instance.role = jsData.role;
  instance.identificationNumber = jsData.identificationNumber;
  instance.hiringAt = applyTimezone(parseDate(jsData.hiringAt), 0);
  instance.layOffAt = applyTimezone(parseDate(jsData.layOffAt), 0);
  instance.payrollDeductions = jsData.payrollDeductions;
  instance.user = parseUser(jsData.user);
  instance.employer = parseEmployer(jsData.employer);
  instance.esocialValidationStatus = jsData.esocialValidationStatus;
  instance.contractId = jsData.contractId;
  instance.creditUpdatedAt = applyTimezone(parseDate(jsData.creditUpdatedAt), 0);
  instance.esocialId = jsData.esocialId;
  instance.salaryNotFound = jsData.salaryNotFound;
  instance.maximumInstallmentAmount = jsData.maximumInstallmentAmount;
  instance.currentMaximumInstallmentAmount = jsData.currentMaximumInstallmentAmount;
  instance.simulationAvailableAt = applyTimezone(parseDate(jsData.simulationAvailableAt), 0);
  instance.paycheckAdvanceAvailableAt = applyTimezone(
    parseDate(jsData.paycheckAdvanceAvailableAt),
    0,
  );
  instance.status = jsData.status;
  instance.corporateEmail = jsData.corporateEmail;
  instance.maximumPaycheckAdvanceAmount = parseNumeric(jsData.maximumPaycheckAdvanceAmount);
  instance.minimumPaycheckAdvanceAmount = parseNumeric(jsData.minimumPaycheckAdvanceAmount);
  instance.freeRequestLimitPaycheckAdvance = parseNumeric(jsData.freeRequestLimitPaycheckAdvance);
  instance.totalLoanDebtBalance = parseNumeric(jsData.totalLoanDebtBalance);
  instance.totalPaycheckAdvanceDebtBalance = parseNumeric(jsData.totalPaycheckAdvanceDebtBalance);
  instance.hasChargingFileDeductions = jsData.hasChargingFileDeductions;
  instance.canChangeChargingFile = jsData.canChangeChargingFile;
  instance.hasProposalInProgress = jsData.hasProposalInProgress;
  instance.department = jsData.department;
  instance.refiCreditBalance = parseNumeric(jsData.refiCreditBalance);
  // DJANGO TIMESTAMPED
  instance.created = parseDate(data.created);
  instance.modified = parseDate(data.modified);
}

function bindDjangoPropsToJs(data: Employee | DjangoEmployee) {
  const djangoData = data as DjangoEmployee;
  const jsData = data as Employee;
  return {
    ...data,
    isActive: djangoData.is_active !== undefined ? djangoData.is_active : jsData.isActive,
    financialConsultantApprovalEnabled:
      djangoData.financial_consultant_approval_enabled != undefined
        ? djangoData.financial_consultant_approval_enabled
        : jsData.financialConsultantApprovalEnabled,
    netEarnings: isNumeric(djangoData.net_earnings) ? djangoData.net_earnings : jsData.netEarnings,
    loanInstallmentsTotal: isNumeric(djangoData.loan_installments_total)
      ? djangoData.net_earnings
      : jsData.loanInstallmentsTotal,
    paycheckAdvancesTotal: isNumeric(djangoData.paycheck_advances_total)
      ? djangoData.net_earnings
      : jsData.paycheckAdvancesTotal,
    score: isNumeric(djangoData.score) ? djangoData.score : jsData.score,
    cluster: isNumeric(djangoData.score) ? djangoData.cluster : jsData.cluster,
    bacen: djangoData.bacen || jsData.bacen,
    concentre: djangoData.concentre
      ? parseDjangoConcentre(djangoData.concentre)
      : parseConcentre(jsData.concentre),
    detailedConcentre: djangoData.detailed_concentre
      ? parseDjangoDetailedConcentre(djangoData.detailed_concentre)
      : parseDetailedConcentre(jsData.detailedConcentre),
    compiledBacen: djangoData.compiled_bacen
      ? parseDjangoCompiledBacen(djangoData.compiled_bacen)
      : parseCompiledBacen(jsData.compiledBacen),
    automaticCreditLimit: isNumeric(djangoData.automatic_credit_limit)
      ? djangoData.automatic_credit_limit
      : jsData.automaticCreditLimit,
    manualCreditLimit: isNumeric(djangoData.manual_credit_limit)
      ? djangoData.manual_credit_limit
      : jsData.manualCreditLimit,
    creditLimit: isNumeric(djangoData.credit_limit) ? djangoData.credit_limit : jsData.creditLimit,
    creditBalance: isNumeric(djangoData.credit_balance)
      ? djangoData.credit_balance
      : jsData.creditBalance,
    debtBalance: isNumeric(djangoData.debt_balance) ? djangoData.debt_balance : jsData.debtBalance,
    paycheckAdvanceBalance: isNumeric(djangoData.paycheck_advance_balance)
      ? djangoData.paycheck_advance_balance
      : jsData.paycheckAdvanceBalance,
    identificationNumber: djangoData.identification_number || jsData.identificationNumber,
    hiringAt: djangoData.hiring_at || jsData.hiringAt,
    layOffAt: djangoData.lay_off_at || jsData.layOffAt,
    payrollDeductions: isNumeric(djangoData.payroll_deductions)
      ? djangoData.payroll_deductions
      : jsData.payrollDeductions,
    esocialValidationStatus: djangoData.esocial_validation_status || jsData.esocialValidationStatus,
    contractId: djangoData.contract_id || jsData.contractId,
    creditUpdatedAt: djangoData.credit_updated_at || jsData.creditUpdatedAt,
    esocialId: djangoData.esocial_id || jsData.esocialId,
    salaryNotFound:
      djangoData.salary_not_found != undefined
        ? djangoData.salary_not_found
        : jsData.salaryNotFound,
    maximumInstallmentAmount: isNumeric(djangoData.maximum_installment_amount)
      ? djangoData.maximum_installment_amount
      : jsData.maximumInstallmentAmount,
    currentMaximumInstallmentAmount: isNumeric(djangoData.current_maximum_installment_amount)
      ? djangoData.current_maximum_installment_amount
      : jsData.currentMaximumInstallmentAmount,
    simulationAvailableAt: djangoData.simulation_available_at || jsData.simulationAvailableAt,
    paycheckAdvanceAvailableAt:
      djangoData.paycheck_advance_available_at || jsData.paycheckAdvanceAvailableAt,
    corporateEmail: djangoData.corporate_email || jsData.corporateEmail,
    maximumPaycheckAdvanceAmount: isNumeric(djangoData.maximum_paycheck_advance_amount)
      ? djangoData.maximum_paycheck_advance_amount
      : jsData.maximumPaycheckAdvanceAmount,
    minimumPaycheckAdvanceAmount: isNumeric(djangoData.minimum_paycheck_advance_amount)
      ? djangoData.minimum_paycheck_advance_amount
      : jsData.minimumPaycheckAdvanceAmount,
    freeRequestLimitPaycheckAdvance: isNumeric(djangoData.free_request_limit_paycheck_advance)
      ? djangoData.free_request_limit_paycheck_advance
      : jsData.freeRequestLimitPaycheckAdvance,
    totalLoanDebtBalance: isNumeric(djangoData.total_loan_debt_balance)
      ? djangoData.total_loan_debt_balance
      : jsData.totalPaycheckAdvanceDebtBalance,
    totalPaycheckAdvanceDebtBalance: isNumeric(djangoData.total_paycheck_advance_debt_balance)
      ? djangoData.total_paycheck_advance_debt_balance
      : jsData.totalPaycheckAdvanceDebtBalance,
    hasChargingFileDeductions:
      djangoData.has_charging_file_deductions !== undefined
        ? djangoData.has_charging_file_deductions
        : jsData.hasChargingFileDeductions,
    canChangeChargingFile:
      djangoData.can_change_charging_file !== undefined
        ? djangoData.can_change_charging_file
        : jsData.canChangeChargingFile,
    hasProposalInProgress:
      djangoData.has_proposal_in_progress !== undefined
        ? djangoData.has_proposal_in_progress
        : jsData.hasProposalInProgress,
    department: djangoData.department || jsData.department,
    refiCreditBalance: isNumeric(djangoData.refi_credit_balance)
      ? djangoData.refi_credit_balance
      : jsData.refiCreditBalance,
  } as Employee;
}
